import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input } from '../../../ui/Inputs';
import { Button } from '../../../ui/Buttons';
import s from './AddValueForm.module.scss';
import { useFormik } from 'formik';
import { AddValueFormProps, AdditionalFieldsValues } from './types';
import { requireSchema } from '../../../../utils/validate';
import { ExclamationIcon } from '../../../ui/Icons';
import { TextEditor } from '../../../ui/TextEditor';
import {
  useAddPhotoToExistingAdditionalFieldMutation,
  useAddPhotoToNewAdditionalFieldMutation,
  useDeletePhotoFromAdditionaFieldMutation
} from '../../../../store/api';
import { useEditorState } from '../../../../hooks';
import { Select } from '../../../ui/Select';
import { ISelectOption } from '../../../ui/Select/DefaultSelect/types';
import { SingleValue } from 'react-select';
import cn from 'classnames';

const AddValueForm: React.FC<AddValueFormProps> = ({
  fieldsLength,
  addAdditionalField,
  editAdditionalField,
  editedAdditionalField,
  script_id,
  //ai_keys,
  onClose
}) => {
  const { t } = useTranslation();
  const [addPhotoToExisting] = useAddPhotoToExistingAdditionalFieldMutation();
  const [addPhotoToNew] = useAddPhotoToNewAdditionalFieldMutation();
  const [deletePhotoInEditor] = useDeletePhotoFromAdditionaFieldMutation();
  const [fieldInitialValues, setFieldInitialValues] = useState({
    name: '',
    text: '',
    //ai_key: ai_keys?.[0] ?? '',
    ai_key: '',
    addtoproject: false
  });
  const orderArr = fieldsLength
    ? Array.from({ length: fieldsLength + 1 }, (_, index) => ({
        label: `${index + 1}`,
        value: fieldsLength + 1 - index
      }))
    : [];

  //const aiKeysArr = ai_keys?.length ? ai_keys.map((item) => ({ label: item, value: item })) : [];

  const [orderId, setOrderId] = useState<ISelectOption | null>(
    fieldsLength ? { label: '1', value: fieldsLength + 1 } : null
  );
  const handleChangeOrderId = (newOrderId: SingleValue<ISelectOption>) => {
    setOrderId(newOrderId);
  };

  const addPhotoInEditorHandle = async (photos: Blob[]) => {
    const response =
      editedAdditionalField &&
      (await addPhotoToExisting({ id: editedAdditionalField?.id, files: photos }).unwrap());
    if (response?.data?.[0]) {
      return response.data[0].url;
    }
  };
  const addNewPhotosInEditorHandle = async (photos: Blob[]) => {
    const response = await addPhotoToNew(photos).unwrap();
    if (response?.data?.[0]) {
      return response.data[0].url;
    }
  };
  const deletePhotoFromEditorHandle = (url: string) => {
    const startIdx = url.indexOf('uploads/');
    const result = url.substring(startIdx);
    if (editedAdditionalField) {
      const deleteImgId = editedAdditionalField.photos?.find((img) => img.url === result)?.id;
      deleteImgId && deletePhotoInEditor({ id: editedAdditionalField.id, photo_id: deleteImgId });
    }
  };

  const formik = useFormik({
    initialValues: fieldInitialValues,
    validationSchema: requireSchema(['name', 'text']),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values: AdditionalFieldsValues) => {
      if (editedAdditionalField) {
        const newValues = Object.values(values).map(String);
        const oldValues = Object.values(fieldInitialValues).map(String);
        const isNotChanged = newValues.every((value, index) => value === oldValues[index]);
        !isNotChanged && editAdditionalField({ ...editedAdditionalField, ...values });
      } else {
        script_id &&
          addAdditionalField({
            ...values,
            script_id,
            set_order_id: orderId ? Number(orderId.value) : 1
          });
      }
      onClose();
    }
  });

  const { editorState, onEditorStateChange, prepareDraft } = useEditorState(
    formik.values.text,
    (value) => formik.setFieldValue('text', value)
  );

  useEffect(() => {
    if (editedAdditionalField) {
      const values = {
        name: editedAdditionalField.name,
        text: editedAdditionalField.text,
        order_id: editedAdditionalField.order_id,
        ai_key: editedAdditionalField.ai_key,
        addtoproject: false
      };
      formik.setValues(values);
      setFieldInitialValues(values);
      onEditorStateChange(prepareDraft(editedAdditionalField.text));
    }
  }, [editedAdditionalField]);

  return (
    <form className={s.form} onSubmit={formik.handleSubmit}>
      <div className={s.fields}>
        <div className={s.field}>
          <Input
            name="name"
            error={!!formik.errors.name}
            onChange={formik.handleChange}
            value={formik.values.name}
            placeholder={`${t('script.value')}`}
            className={s.name}
          />
        </div>
        <div className={s.field}>
          <TextEditor
            value={editorState}
            setValue={onEditorStateChange}
            error={!!formik.errors.text}
            placeholder={`${t('description')}`}
            addPhotos={editedAdditionalField ? addPhotoInEditorHandle : addNewPhotosInEditorHandle}
            deletePhoto={deletePhotoFromEditorHandle}
          />
        </div>
        {/* <div className={cn(s.field, s.ai_key__field)}>
          <span className={s.ai_key__title}>AI-key: </span>
          <Select
            name="ai_key"
            value={aiKeysArr.find((opt) => opt.value === formik.values.ai_key) || aiKeysArr[0]}
            onChange={(option: ISelectOption | null) =>
              formik.setFieldValue('ai_key', option?.value || '')
            }
            options={aiKeysArr}
            className={s.ai_key__select}
          />
        </div> */}
        {!editedAdditionalField && !!orderArr.length && (
          <div className={cn(s.field, s.order__field)}>
            <span className={s.order__title}>Порядок: </span>
            <Select
              name="orderId"
              value={orderId}
              onChange={handleChangeOrderId}
              options={orderArr}
              className={s.order}
            />
          </div>
        )}
      </div>
      <div className={s.btns}>
        <div className={s.addButton__wrapper}>
          <Button className={s.addButton} type="submit" disabled={!script_id}>
            {editedAdditionalField ? t('buttons.edit') : t('buttons.addField')}
          </Button>
          <p className={s.help__text}>{t('script.atFirstCreateScript')}</p>
        </div>
        <Button onClick={onClose} red>
          {t('buttons.cancel')}
        </Button>
        {!editedAdditionalField && (
          <div className={s.checkbox__wrapper}>
            <Checkbox
              label={`${t('script.addtoproject')}`}
              name="addtoproject"
              checked={formik.values.addtoproject}
              value="addtoproject"
              onChange={formik.handleChange}
              className={s.checkbox__values}
              red
            />
            <div className={s.help}>
              <p className={s.help__text}>{t('script.addValueTip')}</p>
              <ExclamationIcon />
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default AddValueForm;
